/**
 * Help & feedback API client surface.
 *
 * User-facing endpoints (any authenticated user):
 *   POST   /api/feedback              - submit
 *   GET    /api/feedback/mine         - list own
 *   PATCH  /api/feedback/{id}         - edit own (while status === 'new')
 *
 * Admin-only (EVO_ADMIN + EVO_TEAM with franchise_partners feature):
 *   GET    /api/admin/feedback
 *   GET    /api/admin/feedback/{id}
 *   PATCH  /api/admin/feedback/{id}        - status and/or triage notes
 *   GET    /api/admin/feedback/counts
 */

import { apiClient } from './api'
import type {
  AdminFeedbackCounts,
  AdminFeedbackDetail,
  AdminFeedbackSummary,
  FeedbackContext,
  FeedbackImpact,
  FeedbackRow,
  FeedbackStatus,
  FeedbackType,
} from '../types/feedback'

export interface SubmitFeedbackInput {
  type: FeedbackType
  impact: FeedbackImpact
  title?: string | null
  details: string
  trying_to_do?: string | null
  follow_up_ok: boolean
  context: FeedbackContext
}

export interface EditFeedbackInput {
  type?: FeedbackType
  impact?: FeedbackImpact
  title?: string | null
  details?: string
  trying_to_do?: string | null
  follow_up_ok?: boolean
}

/**
 * Admin triage edit. PATCH semantics: omit a field to leave it unchanged;
 * send `null`/`''` for a note to clear it.
 */
export interface AdminFeedbackUpdate {
  status?: FeedbackStatus
  update_note?: string | null
  internal_note?: string | null
}

export interface AdminListResponse {
  items: AdminFeedbackSummary[]
  total: number
  page: number
  page_size: number
}

export interface AdminListFilters {
  status?: FeedbackStatus
  type?: FeedbackType
  impact?: FeedbackImpact
  fp_code?: string
  country?: string
  search?: string
  page?: number
  page_size?: number
}

export const feedbackApi = {
  async submit(input: SubmitFeedbackInput): Promise<FeedbackRow> {
    const response = await apiClient.post<FeedbackRow>('/feedback', input)
    return response.data
  },

  async listMine(): Promise<FeedbackRow[]> {
    const response = await apiClient.get<FeedbackRow[]>('/feedback/mine')
    return response.data
  },

  async update(id: number, input: EditFeedbackInput): Promise<FeedbackRow> {
    const response = await apiClient.patch<FeedbackRow>(`/feedback/${id}`, input)
    return response.data
  },
}

export const adminFeedbackApi = {
  async list(filters: AdminListFilters = {}): Promise<AdminListResponse> {
    const params: Record<string, string | number> = {}
    if (filters.status) params.status = filters.status
    if (filters.type) params.type = filters.type
    if (filters.impact) params.impact = filters.impact
    if (filters.fp_code) params.fp_code = filters.fp_code
    if (filters.country) params.country = filters.country
    if (filters.search) params.search = filters.search
    if (filters.page) params.page = filters.page
    if (filters.page_size) params.page_size = filters.page_size

    const response = await apiClient.get<AdminListResponse>('/admin/feedback', { params })
    return response.data
  },

  async get(id: number): Promise<AdminFeedbackDetail> {
    const response = await apiClient.get<AdminFeedbackDetail>(`/admin/feedback/${id}`)
    return response.data
  },

  async update(id: number, input: AdminFeedbackUpdate): Promise<AdminFeedbackDetail> {
    const response = await apiClient.patch<AdminFeedbackDetail>(
      `/admin/feedback/${id}`,
      input,
    )
    return response.data
  },

  async counts(): Promise<AdminFeedbackCounts> {
    const response = await apiClient.get<AdminFeedbackCounts>('/admin/feedback/counts')
    return response.data
  },
}
